import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    STRING_FOR_REGRESSION_STATUS,
    getIsAmazonAppStore,
    getIsAppleAppStore,
    isGooglePlatform,
    oneOfSelectedAppStoreBuildMatchesCIBuild,
} from '../helpers';
import { RootState } from '../reducers/reducers';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import Space from 'antd/lib/space';
import {
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreSelectedBuildReason,
    AppStoreType,
    CIBuildInfo,
    CIBuildSelectionReason,
    IntegrationType,
    QAHistoryItem,
    RegressionBuildInfo,
    RegressionStatus,
    ReleaseCandidateBuildInfo,
} from '../types';
import Tooltip from 'antd/lib/tooltip';
import {
    displayConsoleNameForAppStoreType,
    displayConsoleNameWithPrefix,
    displayWarningMessageForSelectedAppStoreBuilds,
} from '../helpers/displayHelpers';
import { antGrey, runwayColors } from './styles/globalStyles';
import { displayNameForIntegrationType } from '../helpers/integrationHelpers';
import { InfoCircleOutlined, WarningFilled } from '@ant-design/icons';
import { Nullable, ReleaseComponentProps } from '../typescriptHelpers';
import {
    selectHasNonAppStoreBetaIntegration,
    selectIsReleaseRollback,
    selectReleaseDisplayableVersion,
    selectIsWorkflowsCIStepEnabled,
} from '../selectors/selectors';
import { reduce } from 'lodash';
import AppStoreBuildComponent from './appStoreBuilds/AppStoreBuildComponent';
import AutomatedByRunwayIcon from './AutomatedByRunwayIcon';
import Flex from 'antd/lib/flex';
import Alert from 'antd/lib/alert';

interface AppStoreBuildsProps extends ReleaseComponentProps {
    isSubmitted: boolean;
    regressionBuildInfo?: RegressionBuildInfo;
    regressionStatus?: RegressionStatus;
    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    appStoreType: AppStoreType;
    platform: AppPlatform;
    isAppStoreBuildListFilteredByConfiguredTrack?: boolean;
}

const AppStoreBuilds: React.FC<AppStoreBuildsProps> = (props: AppStoreBuildsProps) => {
    const selectedBuilds = props.appStoreSelectedBuilds;

    const additionalSelectedBuilds = props.additionalAppStoreSelectedBuilds;
    const displayableVersion = useSelector((state: RootState) => {
        return selectReleaseDisplayableVersion(state, props.releaseId);
    });

    const hasNonAppStoreBetaIntegration = useSelector((state: RootState) =>
        selectHasNonAppStoreBetaIntegration(state, props.appId),
    );

    const latestSuccessfulCIBuild = (props.latestSuccessfulProdBuild ?? props.latestSuccessfulBuild)?.ciBuild;

    const latestAppStoreBuild = props.appStoreAvailableBuilds?.find((appStoreBuild) => {
        return appStoreBuild.isNewestAppStoreBuild;
    })?.appStoreBuild;

    const activeRCBuild = props.selectedStagingBuild;

    const prodBuild = props.latestSuccessfulProdBuild;

    const isRollback = useSelector((state: RootState) => selectIsReleaseRollback(state, props.releaseId));
    const ciStepWFMigratedAndEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );
    const platform = props.platform;
    const appStoreType = props.appStoreType;
    const isAmazonAppStore = getIsAmazonAppStore(appStoreType);

    const allBuildSelectionWarnings = [];

    const buildsCanTakeAWhileText =
        'Builds can take up to five minutes to show up in Runway after being uploaded to App Store Connect.';

    const additionalBuildsComponent = (additionalSelectedBuildIds: string[], version: string) => {
        return (
            <Space style={{ marginTop: 16 }} size={2}>
                <Text style={{ fontWeight: 600, marginRight: 6 }} type="secondary">
                    Additional builds
                </Text>
                <Tooltip title={`These builds will be included in your ${version} submission.`}>
                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                </Tooltip>
                <Text type="secondary">{`: ${additionalSelectedBuildIds.map((b) => `#${b}`).join(', ')}`}</Text>
            </Space>
        );
    };

    if (!selectedBuilds || selectedBuilds.length === 0) {
        const emptyText =
            getIsAppleAppStore(appStoreType) ?
                `No selected build found. ${buildsCanTakeAWhileText}`
            :   `No selected build found.`;
        return (
            <Space direction="vertical">
                <Text type="secondary">{emptyText}</Text>
                {additionalSelectedBuilds?.length > 0 &&
                    additionalBuildsComponent(additionalSelectedBuilds, displayableVersion)}
            </Space>
        );
    }

    const oneOfSelectedBuildsMatchesLatestCIBuild = reduce(
        selectedBuilds,
        (selectedBuildIsLatest, selectedBuild) => {
            if (selectedBuildIsLatest) {
                return true;
            } else {
                return (
                    selectedBuild.associatedCIBuild &&
                    latestSuccessfulCIBuild &&
                    latestSuccessfulCIBuild.buildIdentifier === selectedBuild.associatedCIBuild.buildIdentifier
                );
            }
        },
        false,
    );

    const oneOfSelectedAppStoreBuildsIsLatest = reduce(
        selectedBuilds,
        (selectedBuildIsLatest, selectedBuild) => {
            if (selectedBuildIsLatest) {
                return true;
            } else {
                return selectedBuild.isNewestAppStoreBuild;
            }
        },
        false,
    );

    const activeRCBuildManualSelection =
        activeRCBuild && activeRCBuild.selectedReason === CIBuildSelectionReason.ManualSelection;

    let outdatedBuildComponent = null;

    if (
        latestSuccessfulCIBuild &&
        !activeRCBuildManualSelection &&
        !oneOfSelectedBuildsMatchesLatestCIBuild &&
        !hasNonAppStoreBetaIntegration // selected app store build will never match RC build in this case
    ) {
        outdatedBuildComponent = (
            <div>
                <Text>
                    A newer{' '}
                    {latestSuccessfulCIBuild.integrationId ?
                        displayNameForIntegrationType(latestSuccessfulCIBuild.integrationId)
                    :   'CI'}{' '}
                    release candidate build may be available (#
                    {latestSuccessfulCIBuild?.buildIdentifier}).
                </Text>
                <Tooltip
                    title={
                        <>
                            Runway was not able to definitively match your latest release candidate build to the
                            selected build in the app store. Typically, this means the selected store build is not the
                            latest build available. In some cases, this may be a false negative.{' '}
                            <Link href={'https://docs.runway.team/using-runway/build-matching'} target="_blank">
                                Read more
                            </Link>
                        </>
                    }
                >
                    <InfoCircleOutlined style={{ color: runwayColors.info }} />
                </Tooltip>
            </div>
        );
    }

    if (latestAppStoreBuild && latestAppStoreBuild.isProcessed && !oneOfSelectedAppStoreBuildsIsLatest) {
        outdatedBuildComponent = (
            <Text>
                A newer{' '}
                {latestAppStoreBuild.integrationId ?
                    displayNameForIntegrationType(latestAppStoreBuild.integrationId)
                :   'app store'}{' '}
                build (#
                {latestAppStoreBuild?.buildNumber}) is available.
            </Text>
        );
    }

    if (outdatedBuildComponent) {
        allBuildSelectionWarnings.push(outdatedBuildComponent);
    }

    if (props.isAppStoreBuildListFilteredByConfiguredTrack) {
        allBuildSelectionWarnings.push(
            <Text>Runway is filtering the list of available builds based on configured track settings.</Text>,
        );
    }

    let ciBuildMismatchComponent = null;

    if (selectedBuilds.length > 0 && activeRCBuild && activeRCBuild.associatedAppStoreBuild) {
        const oneOfSelectAppStoreBuildMatchesActiveRCBuild = oneOfSelectedAppStoreBuildMatchesCIBuild(
            selectedBuilds,
            activeRCBuild,
        );

        if (!oneOfSelectAppStoreBuildMatchesActiveRCBuild && !hasNonAppStoreBetaIntegration) {
            ciBuildMismatchComponent = (
                <Text>
                    {displayWarningMessageForSelectedAppStoreBuilds(
                        selectedBuilds,
                        activeRCBuild,
                        false,
                        ciStepWFMigratedAndEnabled,
                    )}
                </Text>
            );
        }
    }

    // if a prod build is present, override any activeRCBuild mismatch warnings and use prod build instead

    if (prodBuild && prodBuild.associatedAppStoreBuild) {
        const appStoreBuildMatchesProdBuild = oneOfSelectedAppStoreBuildMatchesCIBuild(selectedBuilds, prodBuild);

        ciBuildMismatchComponent =
            !appStoreBuildMatchesProdBuild ?
                <Text>
                    {displayWarningMessageForSelectedAppStoreBuilds(
                        selectedBuilds,
                        prodBuild,
                        true,
                        ciStepWFMigratedAndEnabled,
                    )}
                </Text>
            :   null;
    }

    if (ciBuildMismatchComponent) {
        allBuildSelectionWarnings.push(ciBuildMismatchComponent);
    }

    // if we have regression build info, and at least one selected builds has an associated ci build, check for a match

    const atLeastOneSelectedBuildHasMatchingCIBuild = selectedBuilds.some(
        (selectedBuild) => !!selectedBuild.associatedCIBuild,
    );

    let oneOfSelectedCIBuildsMatchesRegressionBuild = true;

    if (props.regressionBuildInfo && atLeastOneSelectedBuildHasMatchingCIBuild) {
        oneOfSelectedCIBuildsMatchesRegressionBuild = selectedBuilds.some((selectedBuild) => {
            if (selectedBuild.associatedCIBuild) {
                return selectedBuild.associatedCIBuild.buildHash === props.regressionBuildInfo.ciBuild.buildHash;
            }

            return false;
        });
    }

    let isLatestPassingRegressionTestBuild = false;

    if (props.latestPassingRegressionTestBuild) {
        isLatestPassingRegressionTestBuild = selectedBuilds.some((selectedBuild) => {
            if (selectedBuild.associatedCIBuild) {
                return (
                    selectedBuild.associatedCIBuild.buildHash ===
                    props.latestPassingRegressionTestBuild.relatedBuildHash
                );
            }

            return false;
        });
    }

    if (
        props.regressionBuildInfo &&
        !oneOfSelectedCIBuildsMatchesRegressionBuild &&
        !isLatestPassingRegressionTestBuild
    ) {
        const regressionStatus = props.regressionStatus;
        const regressionBuildMistmatchComponent = (
            <Text>
                The selected build doesn&apos;t match the build that was used during regression testing (
                {props.regressionBuildInfo.ciBuild.integrationId ?
                    displayNameForIntegrationType(props.regressionBuildInfo.ciBuild.integrationId)
                :   'CI'}{' '}
                build #{props.regressionBuildInfo.ciBuild.buildIdentifier}
                {regressionStatus && (
                    <>
                        {' '}
                        marked as <strong>{STRING_FOR_REGRESSION_STATUS[regressionStatus]}</strong>
                    </>
                )}
                ).
            </Text>
        );

        allBuildSelectionWarnings.push(regressionBuildMistmatchComponent);
    }

    const appStoreBuilds = (selectedBuilds: AppStoreAvailableBuild[]) => {
        return (
            <Space>
                {selectedBuilds.map((selectedBuild) => {
                    return (
                        <AppStoreBuildComponent
                            {...selectedBuild}
                            key={selectedBuild.appStoreBuild.id}
                            styleOverrides={{
                                backgroundColor: antGrey.grey4,
                                border: `1px solid ${antGrey.grey5}`,
                            }}
                        />
                    );
                })}
            </Space>
        );
    };

    const selectedBuildReasonNode = (selectedBuilds: AppStoreAvailableBuild[]) => {
        if (selectedBuilds.length === 1) {
            const firstSelectedBuild = selectedBuilds.length > 0 ? selectedBuilds[0] : null;

            return displayTextForAppStoreSelectedBuildReason(
                firstSelectedBuild.appStoreBuild.buildNumber,
                firstSelectedBuild.selectedBuildReason,
                firstSelectedBuild.autoSelectedFromTrack,
                platform,
                appStoreType,
                props.isSubmitted,
                isRollback,
                firstSelectedBuild.associatedCIBuild?.integrationId,
            );
        } else if (selectedBuilds.length > 1) {
            return (
                <>
                    {selectedBuilds.map((selectedBuild) => {
                        return displayTextForAppStoreSelectedBuildReason(
                            selectedBuild.appStoreBuild.buildNumber,
                            selectedBuild.selectedBuildReason,
                            selectedBuild.autoSelectedFromTrack,
                            platform,
                            appStoreType,
                            props.isSubmitted,
                            isRollback,
                            selectedBuild.associatedCIBuild?.integrationId,
                        );
                    })}
                </>
            );
        }

        return null;
    };

    const showWarnings = !isAmazonAppStore && !props.isSubmitted;
    const hasWarnings = allBuildSelectionWarnings.length > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Space direction="vertical" size={12}>
                {appStoreBuilds(selectedBuilds)}
                {selectedBuildReasonNode(selectedBuilds)}
            </Space>
            {hasWarnings && showWarnings && (
                <Alert
                    type="warning"
                    message={
                        <Flex vertical>
                            <Space>
                                <WarningFilled style={{ color: runwayColors.statusYellow }} />
                                <Text>Warnings:</Text>
                            </Space>
                            <Flex vertical style={{ marginLeft: 12, marginTop: 12 }}>
                                {allBuildSelectionWarnings.map((warning, index) => {
                                    return (
                                        <Space key={index}>
                                            <Text>-</Text>
                                            {warning}
                                        </Space>
                                    );
                                })}
                            </Flex>
                        </Flex>
                    }
                ></Alert>
            )}
            {additionalSelectedBuilds?.length > 0 &&
                additionalBuildsComponent(additionalSelectedBuilds, displayableVersion)}
        </div>
    );
};

export default AppStoreBuilds;

const displayTextForAppStoreSelectedBuildReason = (
    buildNumber: string,
    selectedBuildReason: AppStoreSelectedBuildReason,
    autoSelectedFromTrack: string | null,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    isSubmitted: boolean,
    isRollback: boolean,
    ciBuildIntegrationType?: IntegrationType,
) => {
    if (isRollback) {
        return null;
    }
    const sourceText =
        getIsAmazonAppStore(appStoreType) ?
            ciBuildIntegrationType ? displayNameForIntegrationType(ciBuildIntegrationType)
            :   'CI'
        :   displayConsoleNameForAppStoreType(appStoreType);

    switch (selectedBuildReason) {
        case AppStoreSelectedBuildReason.Rollback:
            return null;
        case AppStoreSelectedBuildReason.AutoSelection: {
            const endingText =
                isGooglePlatform(platform) && autoSelectedFromTrack ?
                    ` from the configured track '${autoSelectedFromTrack}'`
                :   '';
            return isSubmitted ?
                    <Space>
                        <AutomatedByRunwayIcon />
                        <Text type="secondary">
                            Runway automatically selected build (#{buildNumber}){endingText} for submission.
                        </Text>
                    </Space>
                :   <Space>
                        <AutomatedByRunwayIcon />
                        <Text type="secondary">
                            Runway automatically selected build (#{buildNumber}){endingText}.
                        </Text>
                        <Tooltip title="Any newer builds will be automatically selected, unless an older build is selected manually.">
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    </Space>;
        }
        case AppStoreSelectedBuildReason.ManualRunwaySelection:
            return isSubmitted ?
                    <Text type="secondary">
                        The submitted {displayConsoleNameForAppStoreType(appStoreType)} build (#{buildNumber}) was
                        manually selected in Runway.
                    </Text>
                :   <Space>
                        <Text type="secondary">
                            The selected build (#{buildNumber}) was manually changed in Runway.
                        </Text>
                        <Tooltip
                            title={`Runway will no longer automatically select the latest ${sourceText} build for this release.`}
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    </Space>;
        case AppStoreSelectedBuildReason.ManualStoreSelection:
            return isSubmitted ?
                    <Text type="secondary">
                        The submitted {displayConsoleNameForAppStoreType(appStoreType)} build (#{buildNumber}) was
                        manually selected in {displayConsoleNameWithPrefix(appStoreType)}.
                    </Text>
                :   <Space>
                        <Text type="secondary">
                            The selected {displayConsoleNameForAppStoreType(appStoreType)} build (#{buildNumber}) was
                            manually changed in {displayConsoleNameWithPrefix(appStoreType)}.
                        </Text>
                        <Tooltip
                            title={`Runway will no longer automatically select the latest ${sourceText} build for this release.`}
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    </Space>;
        case AppStoreSelectedBuildReason.ManualRCSelection:
            return isSubmitted ?
                    <Text type="secondary">
                        The submitted {displayConsoleNameForAppStoreType(appStoreType)} build&apos;s (#{buildNumber})
                        associated{' '}
                        {ciBuildIntegrationType ? displayNameForIntegrationType(ciBuildIntegrationType) : 'CI'} build
                        was manually selected in the Release Candidate step.
                    </Text>
                :   <Space>
                        <Text type="secondary">
                            The selected {displayConsoleNameForAppStoreType(appStoreType)} build&apos;s (#{buildNumber})
                            associated{' '}
                            {ciBuildIntegrationType ? displayNameForIntegrationType(ciBuildIntegrationType) : 'CI'}{' '}
                            build was manually selected in the Release Candidate step.
                        </Text>
                        <Tooltip
                            title={`Runway will no longer automatically select the latest ${sourceText} build for this release.`}
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.info }} />
                        </Tooltip>
                    </Space>;
        default:
            return null;
    }
};
